export const GRADE_B_MARKDOWN_KEY = "Pricing:Grade B markdown %";
export const GRADE_C_ONWARDS_MARKDOWN_KEY = "Pricing:Grade C onwards markdown %";
export const DEFAULT_GRADE_B_MARKDOWN_PERCENT = 15;
export const DEFAULT_GRADE_C_ONWARDS_MARKDOWN_PERCENT = 30;

export function parseGradeMarkdownPercent(value: string | null | undefined, fallback: number): number {
  if (value == null || value.trim() === "") return fallback;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) return fallback;
  return parsed;
}

export function hasGrade(grade: string | null | undefined): boolean {
  return !!grade && grade.trim() !== "";
}

export function gradeMarkdownPercent(
  grade: string | null | undefined,
  gradeBPercent: number,
  gradeCOnwardsPercent: number
): number {
  const letter = firstLetter(grade);
  if (letter === "A") return 0;
  if (letter === "B") return gradeBPercent;
  return gradeCOnwardsPercent;
}

export function applyGradeMarkdown(
  intelligentPrice: number | null | undefined,
  grade: string | null | undefined,
  gradeBPercent: number,
  gradeCOnwardsPercent: number
): number | null {
  if (intelligentPrice == null || intelligentPrice <= 0) return null;
  const factor = 1 - gradeMarkdownPercent(grade, gradeBPercent, gradeCOnwardsPercent) / 100;
  return Math.round(intelligentPrice * factor * 100) / 100;
}

export type GradeBand = "A" | "B" | "C";

export interface IGradeAdjustedPrice {
  letter: GradeBand;
  label: string;
  price: number;
}

/** Distinct A / B / C-onwards bands present on the group, in that order. */
export function uniqueGradeBands(grades: Array<string | null | undefined> | null | undefined): GradeBand[] {
  let hasA = false;
  let hasB = false;
  let hasC = false;
  for (const grade of grades ?? []) {
    const letter = firstLetter(grade);
    if (letter === "A") hasA = true;
    else if (letter === "B") hasB = true;
    else if (letter) hasC = true;
  }

  const bands: GradeBand[] = [];
  if (hasA) bands.push("A");
  if (hasB) bands.push("B");
  if (hasC) bands.push("C");
  return bands;
}

/** Average sold (or any base) marked down for each grade on the MPN. */
export function gradeAdjustedPrices(
  basePrice: number | null | undefined,
  grades: Array<string | null | undefined> | null | undefined,
  gradeBPercent: number,
  gradeCOnwardsPercent: number
): IGradeAdjustedPrice[] {
  if (basePrice == null || basePrice <= 0) return [];

  const bands = uniqueGradeBands(grades);
  const use = bands.length > 0 ? bands : (["A", "B", "C"] as GradeBand[]);
  return use
    .map((letter) => {
      const price = applyGradeMarkdown(basePrice, letter, gradeBPercent, gradeCOnwardsPercent);
      if (price == null) return null;
      return { letter, label: letter === "C" ? "C+" : letter, price };
    })
    .filter((row): row is IGradeAdjustedPrice => row != null);
}

function firstLetter(grade: string | null | undefined): string {
  if (!grade || grade.trim() === "") return "";
  return grade.trim()[0].toUpperCase();
}
